/**
 * 删除账户
 */
import { useAsync } from '@tiger/hooks';
import { Form } from 'antd';
import { $Formutil, withForm } from 'react-antd-formutil';
import { Navigate, useNavigate } from 'react-router-dom';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading';
import { useConfirmModal } from 'components/Modal';
import Toast from 'components/Toast';
import security from 'stores/security';
import LogoffBottom from '../components/LogoffBottom';
import { applyOrRevokeDeleteApplication, checkDelete, getDeleteStatus } from '../request';
import './style.scss';

interface DeleteProps {
    $formutil: $Formutil<{
        close_confirm: boolean;
    }>;
}

const Delete: React.FC<DeleteProps> = ({ $formutil }) => {
    const [{ data, loading, error }, getData] = useAsync(() => Promise.all([checkDelete(), getDeleteStatus()]));
    const navigate = useNavigate();
    const { open } = useConfirmModal();

    const submitDelete = async () => {
        try {
            const { data } = await applyOrRevokeDeleteApplication('apply');

            const { success, reasons } = data || {};

            if (success) {
                await Toast.show(__('Submitted successfully'));

                security.setHasAppliedDelete(true);

                if (security.isMarscoAccount) {
                    // marsco账户注销后退出登录
                    window.location.href = '/logout?next=/login';
                } else {
                    // IB账户可以在个人中心，取消申请
                    navigate('/profile');
                }
            } else {
                open({
                    content: (
                        <>
                            <p>{__('尊敬的用户，由于以下原因，暂时无法注销账号，详情请您咨询客服。')}</p>
                            <ul className="pl-4">
                                {reasons?.map((item, index) => (
                                    <li key={index}>
                                        <h4 className="item-title">{item.title}</h4>
                                        <p>{item.content}</p>
                                    </li>
                                ))}
                            </ul>
                        </>
                    ),
                    okText: __('Confirm')
                });
            }
        } catch (error) {
            Toast.show(error);
        }
    };

    const handleSubmit = () => {
        const { $invalid, $getFirstError, $batchState } = $formutil;

        if ($invalid) {
            $batchState({
                $touched: true,
                $dirty: true
            });

            Toast.show($getFirstError());
        } else {
            const content = security.isMarscoAccount
                ? __('Are you sure to cancel your registration with TradeUP.')
                : __(
                      'Are you sure to cancel your registration with TradeUP. You can also choose “Cancel” if you change your mind.'
                  );

            open({
                content,
                okText: __('Next Step'),
                cancelText: __('Cancel'),
                onOk: submitDelete
            });
        }
    };

    const renderMain = () => {
        if (loading) {
            return <Loading />;
        }

        if (error || !data) {
            return <ErrorBox error={error || __('no data')} onClick={getData} />;
        }

        const [checkCloseData, hasApplied] = data;

        const { cancelable, reason } = checkCloseData;

        if (cancelable === false) {
            return <ErrorBox error={reason} />;
        }

        // 如果用户已经提交过申请，我们这里重定向到 个人中心页面
        if (hasApplied) {
            security.setHasAppliedDelete(true);

            return <Navigate to="/profile" replace />;
        }

        return (
            <>
                <ul className="statement-list">
                    <li className="statement-item">
                        {__(
                            'If you decide to cancel your registration with TradeUP. Please click "Next Step" for submitting your request.'
                        )}
                    </li>
                    <li
                        className="statement-item"
                        dangerouslySetInnerHTML={{
                            __html: __(
                                'You can view the status of your request to cancel your registration by receiving email from our Customer Service. If you need assistance, please let us know by contacting our Customer Service through email <a href="mailto:support@tradeup.com">support@tradeup.com</a>. We’d be sad to see you go, but we hope you’ve enjoyed the commission-free investing experience.'
                            )
                        }}
                    />
                </ul>
                <LogoffBottom
                    errorMsg={__('You need to confirm all the information to continue to the next page')}
                    checkBoxLabel={__('I have read information above.')}
                    btnDisabled={!$formutil.$params.close_confirm}
                />
            </>
        );
    };

    return (
        <Form className="logoff-delete-root" onFinish={handleSubmit}>
            {renderMain()}
        </Form>
    );
};

export default withForm(Delete);
